import {arc, pie} from "d3-shape"
import {select, selectAll} from "d3-selection"
import {transition} from "d3-transition"
import {plasmid} from "./modules/plasmid"
import linearPlasmid from "./modules/linearPlasmid"
import {renderVirtualPopper} from "./utils/virtualPopper";
import {fetchAPI} from './utils/requestAPI'

const d3 = Object.assign({}, {
    arc,
    transition,
    pie,
    select,
    selectAll,
    plasmid
});

window.d3 = d3;

const Selector = {
    PLASMID_CONTAINER: '.plasmid-container',
    PLASMID: '.plasmid',
    CONSTRUCT_PLASMID_CONTAINER: '.plasmid-container[data-construct-id]',
    TEMPLATE_PLASMID_CONTAINER: '.plasmid-container[data-template-id]',
    SLOT_DELETE: '.slice-labels text',
    SLOT_APPEND: '.line-labels text',
    MODAL: '#modal'
};

function fetchPlasmidData(e, plasmidChart, url) {
    url = url || plasmidChart.dataUrl();
    return fetchAPI(url)
        .then((response) => {
            let $chart = d3.select(e);
            $chart.datum(response.slots);
            $chart.call(plasmidChart);
        });
}

function getPlasmid(e) {
    return d3.plasmid()
        .width(parseInt(e.dataset.plasmidWidth) || e.offsetWidth)
        .height(parseInt(e.dataset.plasmidHeight) || 300)
        .padAngle(0.005)
}

function loadConstructPlasmid(e, destroy = false) {
    let $plasmid = e.querySelector(Selector.PLASMID),
        slotActive = e.dataset.slotActive,
        constructID = e.dataset.constructId,
        severities = e.dataset.constructSeverities,
        targetUrl = `/constructs/${constructID}/plasmid/`,
        notClickable = $plasmid.classList.contains("no-pointer"),
        plasmidTitle = e.dataset.plasmidTitle,
        fixedSize = e.dataset.plasmidFixedSize !== undefined,
        showDnaSequence = e.dataset.slotShowDnaSequence !== undefined;

    if (destroy) {
        $plasmid.innerHTML = `<span class="spinner-border spinner-border-sm my-auto"></span>`
    }

    let response;
    if ($plasmid.classList.contains('linear')) {
        let $cartoon = new linearPlasmid($plasmid);
        response = fetchAPI(targetUrl).then((data) => {
            $cartoon.severities(severities);
            $cartoon.slotOptions({
                showDnaSequence: showDnaSequence,
                notClickable: notClickable
            })
            $cartoon.showDnaSequence(showDnaSequence);
            $cartoon.current(slotActive);
            $cartoon.fixedSize(fixedSize);
            $cartoon.isEditable(e.dataset.constructEditable !== undefined);
            $cartoon.setData(data);
            $cartoon.render();
        })
    } else {
        let constructPlasmid = getPlasmid(e)
            .current(slotActive)
            .title(plasmidTitle)
            .validationResults(severities)
            .editable(e.dataset.constructEditable)
            .slotOptions({
                showDnaSequence: showDnaSequence,
                notClickable: notClickable
            })
            .dataUrl(targetUrl)
        if (constructPlasmid.editable() === true) {
            constructPlasmid
                .height(600)
                .padAngle(0.025)
                .innerRadius(0.45)
                .outerRadius(0.58)
        }
        response = fetchPlasmidData(
            $plasmid,
            constructPlasmid
        );
    }
    response.then(() => {
        const spinner = e.querySelector('.spinner-border')
        if (spinner)
            spinner.remove()
        e.classList.remove('busy')
        renderVirtualPopper(e.querySelectorAll('[data-toggle="virtual-popper"]'))
    })
}

function loadTemplatePlasmid(e) {
    let $plasmid = e.querySelector(Selector.PLASMID),
        templateId = e.dataset.templateId,
        targetUrl = `/templates/${templateId}/plasmid/`,
        prefixUrl = e.dataset.slotPrefixUrl,
        action = e.dataset.slotAction,
        modal = e.dataset.slotToggleModal !== undefined,
        selectable = e.dataset.slotNeedsSelectable !== undefined,
        manageable = e.dataset.slotNeedsManageable !== undefined,
        current = e.dataset.slotActive,
        notClickable = $plasmid.classList.contains("no-pointer"),
        showDnaSequence = e.dataset.slotShowDnaSequence !== undefined;

    let response;
    if ($plasmid.classList.contains('linear')) {
        response = fetchAPI(targetUrl).then((data) => {
            const $container = e.querySelector(".plasmid");
            let $cartoon = new linearPlasmid($container);
            $cartoon.setDataUrl(prefixUrl);
            $cartoon.showDnaSequence(showDnaSequence);
            $cartoon.current(parseInt(current));
            $cartoon.setAction(action);
            $cartoon.enableModalContent(modal);
            $cartoon.isSelectable(selectable);
            $cartoon.isManageable(manageable);
            $cartoon.slotOptions({
                prefixUrl: prefixUrl,
                toggleModal: modal,
                action: action,
                notClickable: notClickable,
                showDnaSequence: showDnaSequence,
                needsSelectable: selectable,
                needsManageable: manageable,
            })
            $cartoon.setData(data);
            $cartoon.render();

        });
    } else {
        let templatePlasmid = getPlasmid(e)
            .padAngle(0.015)
            .current(current)
            .dataUrl(targetUrl)
            .slotOptions({
                prefixUrl: prefixUrl,
                toggleModal: modal,
                action: action,
                notClickable: notClickable,
                showDnaSequence: showDnaSequence,
                needsSelectable: selectable,
                needsManageable: manageable,
            })
        response = fetchPlasmidData(
            $plasmid,
            templatePlasmid,
        )
    }
    response.then(() => {
        e.classList.remove('busy')
        renderVirtualPopper(e.querySelectorAll('[data-toggle="virtual-popper"]'))
        htmx.process($plasmid)
    });
}

function lazyLoadPlasmid(selector, callback) {
    let options = {
        rootMargin: '0px',
        threshold: 0.1
    }
    let seenEntries = [];
    let observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((e) => {
            if (e.isIntersecting && !seenEntries.includes(e.target.id)) {
                seenEntries.push(e.target.id)
                callback(e.target)
            }
        })
    }, options);
    document.querySelectorAll(selector).forEach((e) => {
        observer.observe(e)
    });
}


document.addEventListener("DOMContentLoaded", function () {

    lazyLoadPlasmid(Selector.CONSTRUCT_PLASMID_CONTAINER, loadConstructPlasmid)
    lazyLoadPlasmid(Selector.TEMPLATE_PLASMID_CONTAINER, loadTemplatePlasmid)

    document.addEventListener('click', function (event) {
        let target = event.target,
            action = target.dataset.slotAction;
        if (action !== undefined) {
            let node = d3.select(event.target),
                data = node.data()[0].data || node.data()[0],
                container = target.closest(Selector.PLASMID_CONTAINER)

            if (action === 'create') {
                fetchAPI('/slots/' + data.id + '/append/', {method: 'POST'})
                    .then(() => {
                        loadConstructPlasmid(container, true)
                    })
                    .catch(() => {
                        alert('Maximum number of slots (placeholders) reached')
                    })
            } else if (action === 'delete') {
                if (confirm(`Delete slot ${data.title}?`)) {
                    fetchAPI('/slots/' + data.id + '/', {method: 'DELETE'})
                        .then(function () {
                            loadConstructPlasmid(container, true)
                        })
                }
            }
        }
    })
});
